/**
 * 邮百工GUI渲染逻辑统一 - 核心表格渲染组件
 * 负责处理Markdown表格的渲染
 */

import React from 'react';
import { preprocessMarkdownTable } from './ContentUtils';

/**
 * 处理表格行数据
 * @param {string} row - 表格行
 * @returns {Array} - 单元格内容数组
 */
const parseTableRow = (row) => {
  if (!row.trim()) return [];
  
  // 移除首尾的 | 字符
  const trimmedRow = row.trim().replace(/^\||\|$/g, '');
  
  // 分割单元格并清理空格
  return trimmedRow.split('|').map(cell => cell.trim());
};

/**
 * 判断是否为分隔行
 * @param {string} row - 表格行
 * @returns {boolean} - 是否为分隔行
 */
const isSeparatorRow = (row) => {
  return row.trim().replace(/\s/g, '').match(/^\|?(:?-+:?\|)+:?-+:?\|?$/);
};

/**
 * 核心表格渲染组件
 * @param {Object} props - 组件属性
 * @param {string} props.content - 表格内容
 * @param {string} props.className - 额外的CSS类名
 * @returns {JSX.Element} - 渲染后的表格组件
 */
const CoreTable = ({ content, className = '' }) => {
  if (!content) return null;
  
  // 预处理表格内容
  const processedContent = preprocessMarkdownTable(content);
  
  // 分割表格行
  const rows = processedContent.split('\n').filter(row => row.trim());
  
  if (rows.length < 2) return null;
  
  // 解析表头
  const headerRow = rows[0];
  const headers = parseTableRow(headerRow);
  
  // 查找分隔行
  let separatorRowIndex = -1;
  rows.forEach((row, index) => {
    if (isSeparatorRow(row)) {
      separatorRowIndex = index;
    }
  });
  
  // 如果没有找到分隔行且行数大于1，则第二行可能是分隔行
  if (separatorRowIndex === -1 && rows.length > 1) {
    separatorRowIndex = 1;
  }
  
  // 解析数据行
  const dataRows = rows.filter((_, index) => 
    index > separatorRowIndex && index < rows.length
  );
  
  // 解析对齐方式
  const alignments = [];
  if (separatorRowIndex !== -1) {
    const separatorRow = rows[separatorRowIndex];
    const separatorCells = parseTableRow(separatorRow);
    
    alignments.push(...separatorCells.map(cell => {
      const trimmedCell = cell.trim();
      
      if (trimmedCell.startsWith(':') && trimmedCell.endsWith(':')) {
        return 'center';
      } else if (trimmedCell.endsWith(':')) {
        return 'right';
      }
      
      return 'left';
    }));
  }
  
  return (
    <div className={`core-table-wrapper ${className}`}>
      <table className="core-table">
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th 
                key={`header-${index}`}
                style={alignments[index] ? { textAlign: alignments[index] } : {}}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rowIndex) => {
            const cells = parseTableRow(row);
            return (
              <tr key={`row-${rowIndex}`}>
                {cells.map((cell, cellIndex) => (
                  <td 
                    key={`cell-${rowIndex}-${cellIndex}`}
                    style={alignments[cellIndex] ? { textAlign: alignments[cellIndex] } : {}}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CoreTable; 